/**
 * Módulo Kanban: quadro, cards, colunas, raias (swimlanes), sprints, labels,
 * métricas de fluxo e o painel de detalhe do card.
 *
 * Os textos nasceram RECORTADOS dos componentes, que escreviam vários rótulos
 * sem acento ("Metricas", "Descricao", "Duracao"). A acentuação foi corrigida
 * aqui: o dicionário é a fonte do texto, e português correto é requisito de
 * produto. Ao adicionar chave nova, escreva acentuado.
 *
 * CHAVE NUNCA É RÓTULO. Tudo que o banco, o `switch` ou o drag-and-drop
 * enxergam continua no componente:
 *  - `tasks.status` / id de coluna (`not_started`, `in_progress`, `completed`,
 *    `paused`, `blocked`) é valor persistido e comparado no drop;
 *  - `sprints.status` (`planning`, `active`, `completed`, `canceled`) idem;
 *  - `tasks.priority` (0..3) e `settings.kanbanGroupBy` idem;
 *  - cor e id de label/coluna são valores, não texto.
 * Onde um desses aparece como chave aqui embaixo, é só para INDEXAR o rótulo.
 */
const kanban = {
  /** Casca do módulo: cabeçalho, filtros, ações e estado vazio. */
  module: {
    title: 'Kanban',
    taskSingular: 'tarefa',
    taskPlural: 'tarefas',
    emptyTitle: 'Nenhuma tarefa no projeto',
    emptyBody: 'Adicione tarefas no Gantt ou crie cards diretamente nas colunas do Kanban.',
    moveError: 'Erro ao salvar a movimentação. Tente novamente.',
    reorderError: 'Erro ao reordenar. Tente novamente.',

    actions: {
      publish: 'Publicar',
      metrics: 'Métricas',
      sprints: 'Sprints',
      labels: 'Labels',
      columns: 'Colunas',
    },

    filters: {
      sprintPlaceholder: 'Sprint',
      sprintAll: 'Todos',
      sprintBacklog: 'Backlog (sem sprint)',
      assigneePlaceholder: 'Responsável',
      assigneeAll: 'Todos',
      priorityPlaceholder: 'Prioridade',
      priorityAll: 'Todas',
      groupPlaceholder: 'Agrupar',
      /* Folha de filtros do celular. O "Agrupar" fica sob rótulo próprio
       * porque NÃO é filtro: é ajuste de visualização, gravado no projeto, e
       * por isso também não entra na contagem do botão. */
      /* Tira do celular quando o projeto esta agrupado: o modo raia nao
       * cabe no telefone, e sumir sem dizer nada deixaria a pessoa achando
       * que o agrupamento se perdeu. */
      semRaiaNoCelular: 'Agrupado por {{grupo}} no computador. Aqui o quadro mostra tudo em colunas.',
      desagrupar: 'Desagrupar',
      sheetTitle: 'Filtros',
      sheetGroupFilters: 'Filtrar por',
      sheetGroupView: 'Visualização',
      sheetClear: 'Limpar',
      sheetApply: 'Ver {{count}} tarefas',
      sheetApplyOne: 'Ver 1 tarefa',
    },

    /** Indexado pelo valor numérico de `tasks.priority` (3, 2, 1, 0). */
    priority: {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Média',
      none: 'Nenhuma',
    },

    /** Indexado pelo valor de `settings.kanbanGroupBy`, que é o que o app grava. */
    groupBy: {
      none: 'Sem agrupamento',
      sprint: 'Por Sprint',
      assignee: 'Por Responsável',
      priority: 'Por Prioridade',
    },

    /** Cabeçalhos das raias. O sublabel do sprint é indexado por `sprints.status`. */
    swimlane: {
      sprintActive: 'Ativo',
      sprintCompleted: 'Concluído',
      sprintPlanning: 'Planejamento',
      backlog: 'Backlog',
      backlogSublabel: 'Sem sprint',
      unassigned: 'Não Atribuído',
    },
  },

  /** Card do quadro: badges, chips de prazo e tooltips. */
  card: {
    /** Indexado pelo valor numérico de `tasks.priority` (1, 2, 3). */
    priority: {
      medium: 'Média',
      high: 'Alta',
      critical: 'Crítica',
    },
    priorityTitle: 'Prioridade: {{level}}',
    milestone: 'Marco',
    blocked: 'Bloqueado',
    overdue: 'Atrasada',
    dueSoon: 'Vence em breve',
    dueDate: 'Data de entrega',
    dependencies: '{{count}} dependência(s)',
    attachments: '{{count}} anexo(s)',
    comments: '{{count}} comentário(s)',
    checklistBadge: 'Checklist: {{done}} de {{total}}',
  },

  /** Coluna do modo plano. O NOME da coluna vem de KANBAN_STATUS_COLUMNS. */
  column: {
    dropHint: 'Arraste cards aqui',
    newCardPlaceholder: 'Nome da tarefa...',
    /** Rodapé da coluna. "tarefa" e não "atividade": o placeholder logo abaixo
     *  já diz "Nome da tarefa..." e o módulo inteiro conta em tarefas. */
    addCard: 'Adicionar tarefa',
    add: 'Adicionar',
    cancel: 'Cancelar',
    useTemplate: 'Criar do template',
    addColumn: 'Nova coluna',
    newColumnPlaceholder: 'Nome da coluna...',
    /* A categoria decide se os cartões desta coluna contam como entregues no
     * Portfólio e no cycle time. Anunciar ANTES de criar é o que evita a
     * pessoa descobrir pelo relatório errado semanas depois. */
    newColumnCategory: 'Entra como {{categoria}}',
  },

  /** Coluna dentro de uma raia: mais compacta, com textos próprios. */
  swimlane: {
    empty: 'Vazio',
    newCardPlaceholder: 'Nome da tarefa...',
    add: 'Adicionar',
    cancel: 'Cancelar',
    addCard: 'Card',
  },

  /**
   * Categoria canônica da coluna (ver `ColumnCategory` em types/database).
   * A CHAVE é valor gravado no banco; só o rótulo muda de idioma.
   */
  categories: {
    not_started: 'A fazer',
    in_progress: 'Em andamento',
    on_hold: 'Parada',
    completed: 'Concluída',
    hint: {
      not_started: 'Zera o progresso. O Portfólio conta como não iniciada.',
      in_progress: 'Começa o progresso em 5% quando o cartão está zerado.',
      on_hold: 'Não mexe no progresso. É onde moram "Pausada" e "Bloqueada".',
      completed: 'Fecha o progresso em 100%. É o que o Portfólio conta como entregue.',
    },
  },

  columnSettings: {
    title: 'Colunas do quadro',
    subtitle: 'A coluna diz onde o cartão está. A categoria diz o que ele significa no Portfólio.',
    newColumnName: 'Nova coluna',
    namePlaceholder: 'Nome da coluna',
    category: 'Categoria',
    addColumn: 'Adicionar coluna',
    cancel: 'Cancelar',
    save: 'Salvar',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    remove: 'Excluir coluna',
    deleteTitle: 'Excluir a coluna "{{name}}"',
    deleteWithCards: {
      one: 'Ela tem {{count}} cartão. Escolha para onde ele vai.',
      other: 'Ela tem {{count}} cartões. Escolha para onde eles vão.',
    },
    deleteEmpty: 'A coluna está vazia.',
    deleteTarget: 'Mover os cartões para',
    deleteConfirm: 'Excluir coluna',
    needsDone: 'O quadro precisa de pelo menos uma coluna na categoria Concluída.',
    needsTodo: 'O quadro precisa de pelo menos uma coluna na categoria A fazer.',
    needsName: 'Toda coluna precisa de um nome.',
    saveError: 'Não foi possível salvar as colunas. Tente novamente.',
    /** `zero` explícito: o CLDR do português classifica 0 como singular e
     *  produziria "0 cartão" (ver a armadilha documentada em i18n/format). */
    cardCount: {
      zero: 'Nenhum cartão nesta coluna',
      one: '{{count}} cartão nesta coluna',
      other: '{{count}} cartões nesta coluna',
    },
  },

  labelSettings: {
    title: 'Gerenciar Labels',
    empty: 'Nenhum label criado.',
    newLabelName: 'Novo Label',
    namePlaceholder: 'Nome do label',
    addLabel: 'Adicionar Label',
    cancel: 'Cancelar',
    save: 'Salvar',
  },

  /**
   * Métricas de fluxo. "Cycle Time", "Throughput" e "Burndown" são nomenclatura
   * Kanban/Scrum: o mercado usa em inglês nos três idiomas, e traduzir deixaria
   * o rótulo brigando com a literatura que o usuário lê fora do produto.
   */
  metrics: {
    title: 'Métricas do Kanban',

    /** Indexado pelo id da aba, que é o que o `activeTab` guarda. */
    tabs: {
      overview: 'Visão Geral',
      cycletime: 'Cycle Time',
      flow: 'Fluxo Cumulativo',
      throughput: 'Throughput',
      burndown: 'Burndown',
    },

    /** Indexado pela janela em dias do filtro (7, 14, 30). */
    periods: {
      d7: '7 dias',
      d14: '14 dias',
      d30: '30 dias',
    },

    avgCycleTime: 'Cycle Time Médio',
    medianCycleTime: 'Cycle Time Mediano',
    completedTasks: 'Tarefas Concluídas',
    lastDays: 'nos últimos {{days}} dias',
    avgThroughput: 'Throughput Médio',
    perWeek: '{{value}}/sem',
    tasksPerWeek: 'tarefas por semana',
    emptyOverview: 'Sem dados de histórico suficientes. Mova tarefas entre colunas para gerar métricas.',

    axisDays: 'Dias',
    tooltipDays: '{{value}} dias',
    tooltipCycleTime: 'Cycle Time',
    emptyCycleTime: 'Sem dados de cycle time. Mova tarefas até a coluna final para gerar dados.',

    emptyFlow: 'Sem dados de fluxo cumulativo.',

    axisWeekOf: 'Semana de',
    tooltipTasks: '{{value}} tarefas',
    seriesCompleted: 'Concluídas',
    emptyThroughput: 'Sem dados de throughput. Conclua tarefas para gerar dados.',

    sprintLabel: 'Sprint:',
    seriesIdeal: 'Ideal',
    seriesRemaining: 'Restante',
    emptyBurndown: 'Sem dados de burndown para o sprint ativo.',
  },

  sprints: {
    title: 'Gerenciar Sprints',
    empty: 'Nenhum sprint criado. Crie seu primeiro sprint para organizar as tarefas.',

    /** Indexado por `sprints.status`, o valor gravado no banco. */
    status: {
      planning: 'Planejamento',
      active: 'Ativo',
      completed: 'Concluído',
      canceled: 'Cancelado',
    },

    namePlaceholder: 'Nome do sprint',
    goalPlaceholder: 'Objetivo do sprint',
    goalPlaceholderNew: 'Objetivo do sprint (opcional)',
    startLabel: 'Início',
    endLabel: 'Fim',
    startPlaceholder: 'Início',
    endPlaceholder: 'Fim',
    start: 'Iniciar Sprint',
    complete: 'Concluir Sprint',
    delete: 'Excluir Sprint',
    deleteConfirm: 'Excluir este sprint? As tarefas voltam para o backlog.',
    create: 'Criar',
    newSprint: 'Novo Sprint',
    cancel: 'Cancelar',
    save: 'Salvar',
    close: 'Fechar',
  },

  /** Painel de detalhe do card. */
  detail: {
    overEstimate: 'acima do estimado',
    groupExecution: 'Execução',
    groupSchedule: 'Cronograma',
    groupEffort: 'Esforço',
    useTemplate: 'Usar template',
    predecessorsAdd: 'Adicionar',
    /**
     * Templates de descrição. O `id` fica no componente; aqui vem o rótulo do
     * botão e o HTML que é injetado no editor, que é conteúdo para o usuário
     * preencher e por isso acompanha o idioma.
     */
    templates: {
      user_story: {
        label: 'User Story',
        html: '<h2>User Story</h2><p><strong>Como</strong> [tipo de usuário],</p><p><strong>Quero</strong> [ação/funcionalidade],</p><p><strong>Para que</strong> [benefício/valor].</p><h2>Critérios de Aceite</h2><ul><li>[ ] </li><li>[ ] </li></ul><h2>Notas</h2><p></p>',
      },
      bug_report: {
        label: 'Bug Report',
        html: '<h2>Descrição do Bug</h2><p></p><h2>Passos para Reproduzir</h2><ol><li></li><li></li><li></li></ol><h2>Comportamento Esperado</h2><p></p><h2>Comportamento Atual</h2><p></p><h2>Ambiente</h2><ul><li>Navegador: </li><li>Sistema: </li></ul>',
      },
      spike: {
        label: 'Spike / Pesquisa',
        html: '<h2>Objetivo</h2><p></p><h2>Perguntas a Responder</h2><ul><li></li><li></li></ul><h2>Restrições</h2><p>Timebox: </p><h2>Resultado Esperado</h2><p></p>',
      },
      task: {
        label: 'Tarefa Técnica',
        html: '<h2>Contexto</h2><p></p><h2>O que fazer</h2><ul><li></li></ul><h2>Definition of Done</h2><ul><li>[ ] Código implementado</li><li>[ ] Testes passando</li><li>[ ] Code review aprovado</li></ul>',
      },
    },

    /** Indexado pelo valor de `tasks.priority` ('0'..'3'). */
    priority: {
      none: 'Nenhuma',
      medium: 'Média',
      high: 'Alta',
      critical: 'Crítica',
    },

    /** `{{field}}` é o nome técnico do campo, que não se traduz. */
    undoField: 'Alterar {{field}}',

    namePlaceholder: 'Nome da tarefa',
    prevTask: 'Tarefa anterior (←)',
    nextTask: 'Próxima tarefa (→)',
    saving: 'Salvando...',
    saved: 'Salvo',

    description: 'Descrição',
    templateLabel: 'Template:',
    templateReplaceTitle: 'Substituir descrição?',
    templateReplaceMessage: 'A descrição atual será substituída pelo template. Essa ação pode ser desfeita com Ctrl+Z no editor.',
    templateReplaceConfirm: 'Substituir',

    templateApplyTitle: 'Aplicar template',
    templateApplyMessage: 'Descrição e checklist atuais serão substituídos pelo conteúdo do template. Prioridade, estimativa e labels também são aplicadas.',
    templateApplyConfirm: 'Aplicar',
    saveAsTemplate: 'Salvar como template',
    templateSaveTitle: 'Salvar como template',
    templateSaveNamePlaceholder: 'Nome do template',
    templateSaveHint: 'Leva descrição, checklist, labels, prioridade e estimativa. Responsáveis e datas ficam de fora.',
    templateSaveCancel: 'Cancelar',
    templateSaveConfirm: 'Salvar',
    templateSaved: 'Template salvo',
    templateSaveError: 'Não foi possível salvar o template',
    cardActions: 'Ações do cartão',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado',
    linkCopyError: 'Não foi possível copiar o link',
    duplicateCard: 'Duplicar cartão',
    deleteCard: 'Excluir cartão',
    duplicated: 'Cartão duplicado',
    deleteConfirmTitle: 'Excluir cartão',
    deleteConfirmMessage: 'Excluir esta tarefa? Subtarefas também serão excluídas. Esta ação não pode ser desfeita.',
    deleteConfirmLabel: 'Excluir',
    descriptionPlaceholder: 'Descreva a tarefa, user story, critérios de aceite... Use @ para mencionar membros',

    attachments: {
      title: 'Anexos',
      empty: 'Nenhum anexo. Arraste um arquivo aqui ou cole um link.',
      addFile: 'Anexar arquivo',
      addLink: 'Adicionar link',
      linkPlaceholder: 'https://...',
      linkInvalid: 'Endereço inválido. Use http, https ou mailto.',
      dropHere: 'Solte para anexar',
      uploading: 'Enviando...',
      download: 'Baixar',
      open: 'Abrir',
      remove: 'Remover',
      removeConfirm: 'Remover este anexo?',
      by: 'por {{name}}',
      mimeNotAllowed: 'Tipo de arquivo não aceito. Envie PDF, Word, Excel, PowerPoint, texto, imagem ou ZIP.',
      /** Cota. O rotulo diz "arquivos anexados" e nunca "armazenamento total":
       *  imagem colada na descricao vive em outro bucket e nao entra na conta,
       *  entao o texto tem que descrever o que a formula realmente mede. */
      quota: {
        usage: '{{used}} de {{limit}} em arquivos anexados',
        nearLimit: 'Falta {{left}} de espaço para anexos.',
        upgradeHint: 'No {{plan}} são {{planLimit}}.',
        seePlans: 'Ver planos',
        blockedTitle: 'Espaço de anexos esgotado',
        blockedBody: 'Você já usou {{used}} dos {{limit}} do plano {{currentPlan}}. Libere espaço removendo anexos antigos, ou mude de plano.',
        fileTooLargeTitle: 'Arquivo grande demais',
        fileTooLargeBody: 'Este arquivo tem {{size}} e o limite por arquivo no plano {{currentPlan}} é {{limit}}. Comprima o arquivo, ou mude de plano.',
        close: 'Fechar',
      },
    },

    checklist: 'Checklist',
    checklistPlaceholder: 'Adicionar item...',
    checklistAdd: 'Adicionar',

    subtasks: 'Subtarefas',
    subtaskAdd: 'Adicionar',
    subtasksEmpty: 'Nenhuma subtarefa. Subtarefas aparecem no Gantt como hierarquia.',
    newSubtask: 'Nova subtarefa',

    status: 'Status',
    statusPlaceholder: 'Selecionar',
    autoBadge: 'Auto',

    sprint: 'Sprint',
    backlog: 'Backlog',

    startLabel: 'Início',
    startPlaceholder: 'Selecionar início',
    endLabel: 'Fim',
    endPlaceholder: 'Selecionar fim',
    durationLabel: 'Duração',
    durationPlaceholder: 'ex: 5d, 16h, 2w',
    durationError: 'Formato: 5d, 16h, 2w, 1m',

    progress: 'Progresso',
    progressAria: 'Progresso da tarefa',

    estimated: 'Estimado',
    actual: 'Real',
    hoursPlaceholder: '0h',

    predecessors: 'Predecessoras',
    predecessorsEmpty: 'Nenhuma',
    edit: 'Editar',

    assignees: 'Responsáveis',
    priorityLabel: 'Prioridade',
    labels: 'Labels',

    blocked: 'Bloqueado',
    blockedAria: 'Tarefa bloqueada',
    yes: 'Sim',
    no: 'Não',
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type kanbanCopy = typeof kanban;

export default kanban;
